import React, { useContext, useEffect, useRef, useState } from 'react'
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Form, FormGroup, Label, Input, FormFeedback,
    Row, Col, Alert, Spinner,
} from 'reactstrap'
import AddressMappingContext from './AddressMappingContext'

const ADDR_FIELDS = [
    { value: '',         label: 'N/A' },
    { value: 'addr1',    label: 'Address 1' },
    { value: 'addr2',    label: 'Address 2' },
    { value: 'city',     label: 'City' },
    { value: 'state',    label: 'State' },
    { value: 'zip',      label: 'Zip' },
]

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS  = 60000

function buildColumnMappings(mappings) {
    const result = {
        t_loc_id_field: null,
        t_loc_nm_field: null,
        address_fields: [null, null, null, null, null], // [addr1, addr2, city, state, zip]
    }
    Object.entries(mappings).forEach(([col, field]) => {
        if (!field) return
        if (field === 't_loc_id')      result.t_loc_id_field    = col
        else if (field === 't_loc_nm') result.t_loc_nm_field    = col
        else if (field === 'addr1')    result.address_fields[0] = col
        else if (field === 'addr2')    result.address_fields[1] = col
        else if (field === 'city')     result.address_fields[2] = col
        else if (field === 'state')    result.address_fields[3] = col
        else if (field === 'zip')      result.address_fields[4] = col
    })
    return result
}

function NewJobWizard({ onClose, onComplete }) {
    const { createJob, uploadFileToS3, getRecord, saveMappings } = useContext(AddressMappingContext)

    const [step, setStep] = useState(1)           // 1 = file+name, 2 = column mapping
    const [jobName, setJobName] = useState('')
    const [file, setFile] = useState(null)
    const [touched, setTouched] = useState(false)
    const [error, setError] = useState(null)
    const [uploading, setUploading] = useState(false)  // uploading to S3
    const [polling, setPolling] = useState(false)      // waiting for lambda to read headers
    const [saving, setSaving] = useState(false)        // saving mappings

    const [jobId, setJobId] = useState(null)
    const [xlsColumns, setXlsColumns] = useState([])
    const [mappings, setMappings] = useState({})

    const pollTimer = useRef(null)
    const pollStart = useRef(null)

    // Cleanup polling on unmount
    useEffect(() => () => clearTimeout(pollTimer.current), [])

    const handleUpload = async () => {
        setTouched(true)
        if (!jobName.trim()) { setError('Reference is required'); return }
        if (!file)           { setError('Please select a file'); return }
        setError(null)
        setUploading(true)
        try {
            const payload = {
                job_type:   'address_mapping',
                job_name:   jobName.trim(),
                file_name:  file.name,
                file_size:  file.size,
                created_by: localStorage.getItem('email') || '',
            }
            const response = await createJob(payload)
            const presignedUrl = response?.presigned_upload_url
            const newJobId = response?.job_id
            if (!presignedUrl || !newJobId) throw new Error('Invalid response from server')

            await uploadFileToS3(presignedUrl, file)
            setJobId(newJobId)
            setUploading(false)
            // Start polling for PENDING_MAPPING status
            setPolling(true)
            pollStart.current = Date.now()
            schedulePoll(newJobId)
        } catch (err) {
            console.error(err)
            setError(err.message || 'Upload failed. Please try again.')
            setUploading(false)
        }
    }

    const schedulePoll = (jid) => {
        pollTimer.current = setTimeout(() => pollForColumns(jid), POLL_INTERVAL_MS)
    }

    const pollForColumns = async (jid) => {
        if (Date.now() - pollStart.current > POLL_TIMEOUT_MS) {
            setPolling(false)
            setError('Timed out waiting for the file to be processed. Please try again.')
            return
        }
        try {
            const record = await getRecord(jid)
            if (record?.status === 'PENDING_MAPPING' && record?.xls_columns?.length > 0) {
                const cols = record.xls_columns
                setXlsColumns(cols)
                const init = {}
                cols.forEach(c => { init[c] = '' })
                setMappings(init)
                setPolling(false)
                setStep(2)
            } else if (record?.status === 'ERROR') {
                setPolling(false)
                setError('The server could not read the uploaded file. Please check the file and try again.')
            } else {
                schedulePoll(jid)
            }
        } catch (err) {
            console.error(err)
            schedulePoll(jid)
        }
    }

    const handleMappingChange = (col, value) => {
        setMappings(prev => ({ ...prev, [col]: value }))
    }

    const handleStartProcessing = async () => {
        setSaving(true)
        setError(null)
        try {
            const columnMappings = buildColumnMappings(mappings)
            await saveMappings(jobId, columnMappings)
            onComplete()
        } catch (err) {
            console.error(err)
            setError(err.message || 'Failed to save mappings. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    const busy = uploading || polling || saving

    return (
        <Modal isOpen centered size="lg" toggle={busy ? undefined : onClose}>
            <ModalHeader toggle={busy ? undefined : onClose}>
                {step === 1
                    ? 'New Upload — Step 1 of 2'
                    : 'Column Mapping — Step 2 of 2'}
            </ModalHeader>

            <div style={{ opacity: saving ? 0.6 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}

                    {/* ── Step 1: job name + file ── */}
                    {step === 1 && (
                        <Form>
                            <FormGroup row>
                                <Label sm={3} className="col-form-label">Reference</Label>
                                <Col sm={9}>
                                    <Input
                                        type="text"
                                        value={jobName}
                                        onChange={e => { setJobName(e.target.value); setError(null) }}
                                        placeholder="Enter a reference for this upload"
                                        invalid={touched && !jobName.trim()}
                                        disabled={busy}
                                    />
                                    <FormFeedback>Reference is required</FormFeedback>
                                </Col>
                            </FormGroup>
                            <FormGroup row>
                                <Label sm={3} className="col-form-label">File</Label>
                                <Col sm={9}>
                                    <Input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={e => { setFile(e.target.files[0]); setError(null) }}
                                        invalid={touched && !file}
                                        disabled={busy}
                                    />
                                    <FormFeedback>Please select an Excel file</FormFeedback>
                                </Col>
                            </FormGroup>

                            {/* Upload + polling status */}
                            {uploading && (
                                <div className="d-flex align-items-center gap-2 text-muted mt-2">
                                    <Spinner size="sm" /> Uploading file...
                                </div>
                            )}
                            {polling && (
                                <div className="d-flex align-items-center gap-2 text-muted mt-2">
                                    <Spinner size="sm" /> Reading column headers from file...
                                </div>
                            )}
                        </Form>
                    )}

                    {/* ── Step 2: column mapping ── */}
                    {step === 2 && (
                        <>
                            <p className="text-muted mb-3">
                                Map each column from your file to the corresponding address field.
                                Unmapped columns will be ignored.
                            </p>
                            <Row className="fw-bold border-bottom pb-2 mb-2 text-muted">
                                <Col sm={6}>XLS Column</Col>
                                <Col sm={6}>Maps To</Col>
                            </Row>
                            {xlsColumns.map(col => (
                                <Row key={col} className="mb-2 align-items-center">
                                    <Col sm={6}><span>{col}</span></Col>
                                    <Col sm={6}>
                                        <Input
                                            type="select"
                                            value={mappings[col] || ''}
                                            onChange={e => handleMappingChange(col, e.target.value)}
                                        >
                                            {ADDR_FIELDS.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            ))}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    {step === 1 && (
                        <>
                            <Button color="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                            <Button color="primary" onClick={handleUpload} disabled={busy}>
                                {uploading ? 'Uploading...' : polling ? 'Reading file...' : 'Upload →'}
                            </Button>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Button color="primary" onClick={handleStartProcessing} disabled={saving}>
                                {saving ? 'Starting...' : 'Start Processing'}
                            </Button>
                        </>
                    )}
                </ModalFooter>
            </div>
        </Modal>
    )
}

export default NewJobWizard
